import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
} from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { Asset } from '@core/generated/gql-types';
import {
  buildChildQuestionMetadata,
  childQuestionValuesFromAsset,
  validateRequiredChildQuestions,
  type ChildQuestionValues,
} from '@core/util/assetMetadata.util';
import type { DocumentSlot } from '@core/util/documentSlots.util';
import type {
  AssetMetadataUpdateParams,
  AssetUploadParams,
} from '@/hooks/useAsset';
import DocumentChildFields from './DocumentChildFields';

export type DocumentDetailsModalState = {
  slot: DocumentSlot;
  asset: Asset | null;
};

interface DocumentDetailsModalProps {
  state: DocumentDetailsModalState | null;
  onClose: () => void;
  onUpload: (params: AssetUploadParams) => Promise<boolean>;
  onUpdateMetadata: (params: AssetMetadataUpdateParams) => Promise<boolean>;
  currentFileName?: string | null;
}

const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png,.heif,.heic';

const DocumentDetailsModal: React.FC<DocumentDetailsModalProps> = ({
  state,
  onClose,
  onUpload,
  onUpdateMetadata,
  currentFileName,
}) => {
  const isOpen = state !== null;
  const isEdit = Boolean(state?.asset);
  const slot = state?.slot;

  const [values, setValues] = useState<ChildQuestionValues>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!state) {
      setValues({});
      setSelectedFile(null);
      setIsSaving(false);
      return;
    }

    setValues(
      childQuestionValuesFromAsset(state.asset, state.slot.childQuestions),
    );
    setSelectedFile(null);
    setIsSaving(false);
  }, [state]);

  const handleValueChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    setSelectedFile(file);
  };

  const hasChildQuestions = (slot?.childQuestions.length ?? 0) > 0;

  const handleSave = async () => {
    if (!slot) return;

    const validationError = validateRequiredChildQuestions(
      values,
      slot.childQuestions,
    );
    if (validationError) {
      toast.error(validationError, {
        position: 'bottom-left',
        autoClose: 4000,
      });
      return;
    }

    if (!isEdit && !selectedFile) {
      toast.error('Please choose a file to upload', {
        position: 'bottom-left',
        autoClose: 4000,
      });
      return;
    }

    const metadata = buildChildQuestionMetadata(values, slot.childQuestions, {
      includeEmpty: isEdit && !selectedFile,
    });
    const hasMetadata = Object.keys(metadata).length > 0;

    setIsSaving(true);
    try {
      let success = false;

      if (selectedFile) {
        success = await onUpload({
          tag: slot.tag,
          file: selectedFile,
          metadata: hasMetadata ? metadata : undefined,
        });
      } else if (isEdit) {
        if (!hasChildQuestions) {
          onClose();
          return;
        }
        success = await onUpdateMetadata({
          tag: slot.tag,
          metadata,
        });
      }

      if (success) {
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          if (!isSaving) {
            onClose();
          }
        }}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
            >
              <DialogPanel className="flex w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                  <DialogTitle
                    as="h3"
                    className="text-lg font-medium text-gray-900"
                  >
                    {slot?.title ?? 'Document details'}
                  </DialogTitle>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Close"
                    disabled={isSaving}
                  >
                    ✕
                  </button>
                </div>

                <div className="flex flex-col gap-4 px-4 py-4">
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-medium text-gray-600">
                      File
                      {!isEdit && (
                        <span className="text-red-500" aria-hidden="true">
                          {' '}
                          *
                        </span>
                      )}
                    </span>
                    {isEdit && !selectedFile && (
                      <p className="text-xs text-gray-500">
                        Current file:{' '}
                        <span className="font-medium text-gray-700">
                          {currentFileName || 'Uploaded document'}
                        </span>
                      </p>
                    )}
                    {selectedFile && (
                      <p className="text-xs text-gray-700">
                        Selected: {selectedFile.name}
                      </p>
                    )}
                    <label
                      className={`flex cursor-pointer items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 ${isSaving ? 'pointer-events-none opacity-60' : ''}`}
                    >
                      <FontAwesomeIcon icon={faUpload} className="h-4 w-4" />
                      {isEdit
                        ? selectedFile
                          ? 'Choose a different file'
                          : 'Replace file'
                        : 'Choose file'}
                      <input
                        type="file"
                        accept={ACCEPTED_FILE_TYPES}
                        className="hidden"
                        disabled={isSaving}
                        onChange={handleFileChange}
                      />
                    </label>
                    <p className="text-xs text-gray-400">
                      PDF, JPG, JPEG, PNG, HEIF, and HEIC are supported.
                    </p>
                  </div>

                  {hasChildQuestions && (
                    <div className="flex flex-col gap-2 border-t border-gray-100 pt-4">
                      <span className="text-xs font-medium text-gray-600">
                        Details
                      </span>
                      <DocumentChildFields
                        childQuestions={slot!.childQuestions}
                        values={values}
                        onChange={handleValueChange}
                        disabled={isSaving}
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSaving}
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSave()}
                    disabled={isSaving}
                    className="rounded-md border border-gray-300 bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DocumentDetailsModal;
